import { useEffect, useRef, useState } from 'react';
import { App, Button, Form, Input } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { useLinkWithPasskey, useLoginWithOAuth, useLoginWithPasskey, usePrivy } from '@privy-io/react-auth';
import type { User } from '@privy-io/react-auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppApc, type RedPacket } from '../../api/appApc';
import { getApiErrorMessage, useAuth } from '../../auth/AuthContext';
import {
    getSession,
    hasPasskeyLoginSuccess,
    isSessionValid,
    markPasskeyLoginSuccess,
    markPasskeySetup,
} from '../../auth/storage';
import { formatNumber } from '../../i18n/format';
import { readWalletAmounts } from '../Wallet/walletData';
import './RedPacket.css';
import './Open.css';

type Screen = 'open' | 'auth' | 'pending' | 'available' | 'expired' | 'full' | 'ineligible';

type LoginForm = {
    email: string;
    password: string;
};

type AuthApiPayload = {
    success?: boolean;
    message?: string;
    accessToken?: string;
};

type PasskeyGate = {
    settled: boolean;
    seenChallenge: boolean;
    resolve: () => void;
    reject: (error: unknown) => void;
};

const TEMPLATE_IDS: Record<string, string> = {
    CLASSIC: 'classic',
    APC_MINT: 'mint',
    MIDNIGHT: 'night',
    BIRTHDAY: 'birthday',
    LUNAR_NEW_YEAR: 'lunar',
    THANK_YOU: 'thanks',
    TEAM_CHEERS: 'team',
};

const SYMBOLS: Record<string, string> = {
    classic: '✧',
    mint: '+',
    night: '✦',
    birthday: '◉',
    lunar: '✺',
    thanks: '♥',
    team: '✦',
};

function isSuccessResponse(response: { status: number; data?: AuthApiPayload }) {
    const httpOk = response.status === 200 || response.status === 201;
    const data = response.data;
    if (data?.accessToken) return true;
    return httpOk && data?.success !== false;
}

function passkeyIdsOf(user: User | null | undefined) {
    return (
        user?.linkedAccounts.flatMap((account) =>
            account.type === 'passkey' ? [account.credentialId] : []
        ) ?? []
    );
}

function passkeyAccountKeys(user: User | null | undefined) {
    const keys: string[] = [];
    if (user?.id) keys.push(user.id);
    const email = user?.email?.address || getSession()?.email;
    if (typeof email === 'string' && email.trim()) keys.push(email.trim().toLowerCase());
    return keys;
}

function delay(ms: number) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function screenFromStatus(status: string): Screen | null {
    const value = status.trim().toLowerCase();
    if (value === 'expired' || value === 'expire') return 'expired';
    if (value === 'fullclaim' || value === 'full' || value === 'fully_claimed' || value === 'fullyclaimed') {
        return 'full';
    }
    if (value === 'ineligible' || value === 'not_eligible' || value === 'notinlist' || value === 'limited') {
        return 'ineligible';
    }
    return null;
}

function screenFromPacket(packet: RedPacket): Screen {
    if (packet.status === 'EXPIRED' || packet.status === 'CANCELLED' || packet.status === 'FAILED') return 'expired';
    if (packet.myClaim?.status === 'CREDITED') return 'available';
    if (packet.myClaim) return 'pending';
    if (packet.status === 'FULLY_CLAIMED' || packet.remainingCount <= 0) return 'full';
    if (!packet.canClaim) return 'ineligible';
    return 'open';
}

function money(value: string | number | undefined) {
    const amount = typeof value === 'number' ? value : Number(value || 0);
    return formatNumber(Number.isFinite(amount) ? amount : 0, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function OpenPacket({ token, status }: { token: string; status: string }) {
    const { t } = useTranslation('redPacket');
    const { t: authT } = useTranslation('auth');
    const navigate = useNavigate();
    const { message } = App.useApp();
    const {
        login,
        loginWithGoogle,
        logout,
        startPasskeyGate,
        finishPasskeyGate,
        refreshProfile,
        isAuthenticated,
        authInitializing,
        passkeyGateOpen,
        user,
    } = useAuth();
    const { ready, authenticated, user: privyUser } = usePrivy();
    const { loginWithPasskey, state: passkeyState } = useLoginWithPasskey();
    const { linkWithPasskey } = useLinkWithPasskey();
    const { initOAuth, loading: googleLoading } = useLoginWithOAuth({
        onComplete: async ({ loginMethod }) => {
            if (loginMethod !== 'google') return;
            setGoogleBackendSyncing(true);
            startPasskeyGate();
            flowRef.current = true;
            try {
                await loginWithGoogle();
                const ok = await completeSignInRef.current();
                if (!ok) {
                    flowRef.current = false;
                    return;
                }
                message.success(authT('login.googleSuccess'));
                await revealPacketRef.current();
            } catch (error) {
                console.log('error', error);
                finishPasskeyGate();
                flowRef.current = false;
                message.error(getApiErrorMessage(error, authT('errors.google')));
            } finally {
                setGoogleBackendSyncing(false);
            }
        },
        onError: (error) => {
            console.log('error', error);
            message.error(getApiErrorMessage(error, authT('errors.google')));
        },
    });
    const [screen, setScreen] = useState<Screen>(screenFromStatus(status) ?? 'auth');
    const [packet, setPacket] = useState<RedPacket | null>(null);
    const [balance, setBalance] = useState<number | null>(null);
    const [opening, setOpening] = useState(false);
    const [claimLoading, setClaimLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [googleBackendSyncing, setGoogleBackendSyncing] = useState(false);
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const flowRef = useRef(false);
    const privySnapshot = useRef({ ready, authenticated, user: privyUser });
    const passkeyGate = useRef<PasskeyGate | null>(null);
    const linkingPasskey = useRef(false);
    const resumeGate = useRef(passkeyGateOpen);
    const loginWithPasskeyRef = useRef(loginWithPasskey);
    const linkWithPasskeyRef = useRef(linkWithPasskey);
    const revealPacketRef = useRef<() => Promise<void>>(async () => undefined);
    const completeSignInRef = useRef<() => Promise<boolean>>(async () => false);
    privySnapshot.current = { ready, authenticated, user: privyUser };
    loginWithPasskeyRef.current = loginWithPasskey;
    linkWithPasskeyRef.current = linkWithPasskey;

    useEffect(() => {
        const gate = passkeyGate.current;
        if (linkingPasskey.current || !gate || gate.settled) return;
        if (passkeyState.status === 'generating-challenge' || passkeyState.status === 'awaiting-passkey') {
            gate.seenChallenge = true;
            return;
        }
        if (!gate.seenChallenge) return;
        if (passkeyState.status === 'submitting-response' || passkeyState.status === 'done') {
            gate.settled = true;
            passkeyGate.current = null;
            markPasskeySetup();
            gate.resolve();
            return;
        }
        if (passkeyState.status === 'error') {
            gate.settled = true;
            passkeyGate.current = null;
            gate.reject(passkeyState.error ?? new Error('passkey'));
        }
    }, [passkeyState]);

    const applyPacket = (next: RedPacket) => {
        setPacket(next);
        if (!flowRef.current) setScreen(screenFromPacket(next));
    };

    useEffect(() => {
        if (authInitializing || !isAuthenticated || flowRef.current) return undefined;
        let cancelled = false;
        AppApc.GetRedPacketByToken(token)
            .then((response) => {
                if (cancelled) return;
                applyPacket(response.data.data);
            })
            .catch((error) => {
                if (cancelled) return;
                if (error?.status === 401) {
                    logout();
                    setScreen(screenFromStatus(status) ?? 'auth');
                    return;
                }
                message.error(getApiErrorMessage(error, t('page.errors.load')));
            });
        return () => {
            cancelled = true;
        };
    }, [authInitializing, isAuthenticated, token]);

    const waitForPrivyUser = async () => {
        const started = Date.now();
        let stableId = '';
        let stableSince = 0;
        while (Date.now() - started < 12000) {
            const current = privySnapshot.current;
            const id = current.user?.id ?? '';
            if (current.ready && current.authenticated && current.user) {
                if (id !== stableId) {
                    stableId = id;
                    stableSince = Date.now();
                } else if (Date.now() - stableSince >= 500) {
                    return current.user;
                }
            }
            await delay(200);
        }
        return privySnapshot.current.user;
    };

    const verifyExistingPasskey = (credentialIds: string[]) =>
        new Promise<void>((resolve, reject) => {
            const gate: PasskeyGate = { settled: false, seenChallenge: false, resolve, reject };
            passkeyGate.current = gate;
            void loginWithPasskeyRef.current({ credentialIds }).catch((error) => {
                if (gate.settled) return;
                gate.settled = true;
                if (passkeyGate.current === gate) passkeyGate.current = null;
                reject(error);
            });
        });

    const setupPasskey = async () => {
        linkingPasskey.current = true;
        try {
            await linkWithPasskeyRef.current({ name: 'APC Plus passkey' });
        } finally {
            linkingPasskey.current = false;
        }
        markPasskeySetup();
    };

    const completeSignIn = async () => {
        let settingUp = false;
        setStep(2);
        try {
            const currentUser = await waitForPrivyUser();
            const accountKeys = passkeyAccountKeys(currentUser);
            const credentialIds = passkeyIdsOf(currentUser);
            const knownDevice = hasPasskeyLoginSuccess(accountKeys);
            if (knownDevice) {
                if (!credentialIds.length) throw new Error(authT('errors.passkey'));
                message.info(authT('login.verifyingPasskey'));
                await verifyExistingPasskey(credentialIds);
            } else {
                if (!privySnapshot.current.authenticated) throw new Error(authT('errors.passkeySession'));
                settingUp = true;
                message.info(credentialIds.length ? authT('login.setupPasskeyOnDevice') : authT('login.setupPasskey'));
                await setupPasskey();
            }
            markPasskeyLoginSuccess(accountKeys);
            finishPasskeyGate();
            return true;
        } catch (error) {
            console.log('error', error);
            logout();
            setStep(1);
            setScreen('auth');
            message.error(getApiErrorMessage(error, settingUp ? authT('errors.passkeySetup') : authT('errors.passkey')));
            return false;
        }
    };
    completeSignInRef.current = completeSignIn;

    const handleGoogleLogin = async () => {
        try {
            await initOAuth({ provider: 'google' });
        } catch (error) {
            console.log('error', error);
            message.error(getApiErrorMessage(error, authT('errors.google')));
        }
    };

    const claimPacket = async (current: RedPacket) => {
        setOpening(true);
        setClaimLoading(false);
        let pending = true;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const wait = new Promise<void>((resolve) => {
            window.setTimeout(() => {
                if (pending) setClaimLoading(true);
                resolve();
            }, reduceMotion ? 180 : 1680);
        });
        try {
            const response = await Promise.all([
                AppApc.ClaimRedPacket({ id: current.id }).finally(() => {
                    pending = false;
                }),
                wait,
            ]).then(([result]) => result);
            const next = response.data.data;
            setPacket(next);
            const profile = await refreshProfile();
            if (profile) setBalance(readWalletAmounts(profile).availableCredits);
            else if (user) setBalance(readWalletAmounts(user).availableCredits);
            setStep(3);
            setScreen(screenFromPacket(next));
        } catch (error) {
            console.log('error', error);
            message.error(getApiErrorMessage(error, t('page.cannotClaim')));
            if ((error as { status?: number })?.status === 401) {
                logout();
                setScreen('auth');
                return;
            }
            setScreen('open');
        } finally {
            setOpening(false);
            setClaimLoading(false);
        }
    };

    const revealPacket = async () => {
        flowRef.current = true;
        try {
            const response = await AppApc.GetRedPacketByToken(token);
            const next = response.data.data;
            setPacket(next);
            setOpening(false);
            setScreen(screenFromPacket(next));
        } catch (error) {
            console.log('error', error);
            message.error(getApiErrorMessage(error, t('page.errors.load')));
            setScreen('auth');
        } finally {
            flowRef.current = false;
        }
    };
    revealPacketRef.current = revealPacket;

    useEffect(() => {
        if (!resumeGate.current) return;
        resumeGate.current = false;
        if (!isSessionValid()) {
            finishPasskeyGate();
            return;
        }
        void (async () => {
            const ok = await completeSignIn();
            if (ok) await revealPacketRef.current();
        })();
    }, []);

    const onFinish = async ({ email, password }: LoginForm) => {
        setSubmitting(true);
        startPasskeyGate();
        flowRef.current = true;
        try {
            const response = await login(email, password, true);
            if (!isSuccessResponse(response)) {
                logout();
                message.error(getApiErrorMessage(response.data, authT('errors.signIn')));
                return;
            }
            const ok = await completeSignIn();
            if (!ok) {
                flowRef.current = false;
                return;
            }
            message.success(authT('login.success'));
            await revealPacket();
        } catch (error) {
            console.log('error', error);
            finishPasskeyGate();
            flowRef.current = false;
            message.error(getApiErrorMessage(error, authT('errors.signIn')));
        } finally {
            setSubmitting(false);
        }
    };

    const onOpen = () => {
        if (opening || passkeyGateOpen) return;
        if (!isAuthenticated) {
            setStep(1);
            setScreen('auth');
            return;
        }
        if (!packet || screenFromPacket(packet) !== 'open') {
            if (packet) setScreen(screenFromPacket(packet));
            return;
        }
        flowRef.current = true;
        void claimPacket(packet).finally(() => {
            flowRef.current = false;
        });
    };

    const refreshTransfer = async () => {
        try {
            const response = await AppApc.GetRedPacketByToken(token);
            const next = response.data.data;
            setPacket(next);
            const profile = await refreshProfile();
            if (profile) setBalance(readWalletAmounts(profile).availableCredits);
            setScreen(screenFromPacket(next));
        } catch (error) {
            message.error(getApiErrorMessage(error, t('page.errors.load')));
        }
    };

    const sender = packet?.creatorDisplayName || 'APC Plus';
    const templateId = TEMPLATE_IDS[packet?.template || 'CLASSIC'] || 'classic';
    const expiresLabel = packet?.expiresAt ? expiryLabel(packet.expiresAt, t) : '';
    const distribution = packet?.split === 'EQUAL' ? t('openLink.equal') : t('openLink.random');
    const audience = packet?.audience === 'CIRCLE' ? packet.circleName || t('openLink.circle') : t('openLink.sharedLink');
    const received = money(packet?.myClaim?.amount);
    const shownBalance = balance ?? (user ? readWalletAmounts(user).availableCredits : 0);
    const passkeyReady = isAuthenticated && !passkeyGateOpen && !authInitializing;
    const terminalView = screen === 'expired' || screen === 'full' || screen === 'ineligible';
    const view: Screen = !passkeyReady ? (terminalView ? screen : 'auth') : screen === 'auth' ? 'open' : screen;
    const canClaimNow = Boolean(packet && screenFromPacket(packet) === 'open');

    return (
        <div className="rp-open-page">
            <section className="rp-open-card">
                <div className="rp-open-visual">
                    <p>{t('openLink.fromKicker', { name: sender })}</p>
                    <div
                        className={`rp-envelope ${templateId}${opening ? ' is-opening' : ''}`}
                        role="img"
                        aria-label={sender}
                    >
                        <span className="rp-envelope-flap" aria-hidden />
                        <span className="rp-envelope-brand">APC PLUS</span>
                        <span className="rp-envelope-seal" aria-hidden>
                            {SYMBOLS[templateId] || '✧'}
                        </span>
                        <small>{t(`page.templates.${templateId}.tagline`)}</small>
                        {claimLoading ? (
                            <span className="rp-envelope-loading" role="status">
                                <span className="rp-envelope-spinner" aria-hidden />
                            </span>
                        ) : null}
                    </div>
                    <div className="rp-open-pills">
                        {view === 'expired' ? (
                            <span>{t('openLink.expiredPill')}</span>
                        ) : expiresLabel ? (
                            <span>{expiresLabel}</span>
                        ) : null}
                        <span>{audience}</span>
                    </div>
                </div>
                <div className="rp-open-detail">
                    {view === 'auth' ? (
                        <AuthPanel
                            step={step}
                            submitting={submitting}
                            googleLoading={googleLoading || googleBackendSyncing}
                            onFinish={onFinish}
                            onGoogle={handleGoogleLogin}
                            t={t}
                            authT={authT}
                        />
                    ) : null}
                    {view === 'open' ? (
                        <div className="rp-open-copy">
                            <p className="rp-open-kicker">{t('openLink.kicker')}</p>
                            <h1>{packet?.message || t('openLink.fallbackMessage')}</h1>
                            <p className="rp-open-lead">{t('openLink.lead', { name: sender })}</p>
                            <dl>
                                <div>
                                    <dt>{t('openLink.from')}</dt>
                                    <dd>{sender}</dd>
                                </div>
                                <div>
                                    <dt>{t('openLink.distribution')}</dt>
                                    <dd>{distribution}</dd>
                                </div>
                                <div>
                                    <dt>{t('openLink.expires')}</dt>
                                    <dd>{expiresLabel || '—'}</dd>
                                </div>
                            </dl>
                            <button className="rp-open-primary" type="button" onClick={onOpen} disabled={opening || !canClaimNow}>
                                {opening ? t('openLink.opening') : t('openLink.open')}
                            </button>
                        </div>
                    ) : null}
                    {view === 'pending' ? (
                        <div className="rp-open-copy">
                            <Progress current={3} t={t} />
                            <p className="rp-open-kicker">{t('openLink.pending.kicker')}</p>
                            <h1>{t('openLink.pending.title')}</h1>
                            <p className="rp-open-amount">
                                {received} <small>{t('openLink.pending.unit')}</small>
                            </p>
                            <p className="rp-open-lead">{t('openLink.pending.lead')}</p>
                            <ol className="rp-open-checks">
                                <li className="is-done">
                                    <span />
                                    <strong>{t('openLink.pending.assigned')}</strong>
                                    <small>{t('openLink.pending.assignedHint')}</small>
                                </li>
                                <li className="is-current">
                                    <span />
                                    <strong>{t('openLink.pending.transfer')}</strong>
                                    <small>{t('openLink.pending.transferHint')}</small>
                                </li>
                                <li>
                                    <span />
                                    <strong>{t('openLink.pending.balance')}</strong>
                                    <small>{t('openLink.pending.balanceHint')}</small>
                                </li>
                            </ol>
                            <button className="rp-open-primary" type="button" onClick={() => void refreshTransfer()}>
                                {t('openLink.pending.action')}
                            </button>
                        </div>
                    ) : null}
                    {view === 'available' ? (
                        <div className="rp-open-copy">
                            <span className="rp-open-badge" aria-hidden>
                                ✓
                            </span>
                            <p className="rp-open-kicker">{t('openLink.done.kicker')}</p>
                            <h1>{t('openLink.done.title', { amount: received })}</h1>
                            <p className="rp-open-lead">{t('openLink.done.lead')}</p>
                            <dl>
                                <div>
                                    <dt>{t('openLink.from')}</dt>
                                    <dd>{sender}</dd>
                                </div>
                                <div>
                                    <dt>{t('openLink.done.received')}</dt>
                                    <dd>
                                        {received} {t('openLink.pending.unit')}
                                    </dd>
                                </div>
                                <div>
                                    <dt>{t('openLink.done.balance')}</dt>
                                    <dd>
                                        {money(shownBalance)} {t('openLink.pending.unit')}
                                    </dd>
                                </div>
                                <div>
                                    <dt>{t('openLink.done.status')}</dt>
                                    <dd>{t('openLink.done.confirmed')}</dd>
                                </div>
                            </dl>
                            <button className="rp-open-primary" type="button" onClick={() => navigate('/wallet')}>
                                {t('openLink.done.wallet')}
                            </button>
                        </div>
                    ) : null}
                    {view === 'expired' ? (
                        <Terminal
                            icon="hourglass"
                            kicker={t('openLink.expired.kicker')}
                            title={t('openLink.expired.title')}
                            lead={t('openLink.expired.lead', { name: sender })}
                            action={t('openLink.expired.back')}
                            onAction={() => navigate('/home')}
                        />
                    ) : null}
                    {view === 'full' ? (
                        <Terminal
                            icon="ring"
                            kicker={t('openLink.full.kicker')}
                            title={t('openLink.full.title')}
                            lead={t('openLink.full.lead')}
                            action={t('openLink.full.back')}
                            onAction={() => navigate('/home')}
                        />
                    ) : null}
                    {view === 'ineligible' ? (
                        <Terminal
                            icon="ring"
                            kicker={t('openLink.ineligible.kicker')}
                            title={t('openLink.ineligible.title')}
                            lead={t('openLink.ineligible.lead')}
                            action={t('openLink.ineligible.action')}
                            onAction={() => {
                                logout();
                                setStep(1);
                                setScreen('auth');
                            }}
                        />
                    ) : null}
                </div>
            </section>
        </div>
    );
}

function expiryLabel(expiresAt: string, t: (key: string, options?: Record<string, unknown>) => string) {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (!Number.isFinite(ms) || ms <= 0) return t('openLink.expiredPill');
    const hours = Math.max(1, Math.ceil(ms / 3600000));
    if (hours >= 48) return t('openLink.days', { count: Math.ceil(hours / 24) });
    return t('openLink.hours', { count: hours });
}

function Progress({ current, t }: { current: 1 | 2 | 3; t: (key: string) => string }) {
    const items = [
        t('openLink.steps.account'),
        t('openLink.steps.verify'),
        t('openLink.steps.receive'),
    ];
    return (
        <ol className="rp-open-steps">
            {items.map((label, index) => {
                const number = index + 1;
                const state = number < current ? 'is-done' : number === current ? 'is-current' : '';
                return (
                    <li key={label} className={state}>
                        <span>{number < current ? '✓' : number}</span>
                        {label}
                    </li>
                );
            })}
        </ol>
    );
}

function GoogleMark() {
    return (
        <svg className="rp-open-google-mark" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.21 3.31v2.77h3.57C21.36 18.42 22.56 15.6 22.56 12.25z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.49 12c0-.72.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.93l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
    );
}

function AuthPanel({
    step,
    submitting,
    googleLoading,
    onFinish,
    onGoogle,
    t,
    authT,
}: {
    step: 1 | 2 | 3;
    submitting: boolean;
    googleLoading: boolean;
    onFinish: (values: LoginForm) => Promise<void>;
    onGoogle: () => void;
    t: (key: string) => string;
    authT: (key: string) => string;
}) {
    return (
        <div className="rp-open-copy">
            <Progress current={step} t={t} />
            <p className="rp-open-kicker">{t('openLink.auth.kicker')}</p>
            <h1>{t('openLink.auth.title')}</h1>
            <p className="rp-open-lead">{t('openLink.auth.lead')}</p>
            <Button
                className="rp-open-google"
                htmlType="button"
                icon={<GoogleMark />}
                loading={googleLoading}
                disabled={submitting || googleLoading}
                onClick={onGoogle}
            >
                {authT('login.google')}
            </Button>
            <p className="rp-open-or">{authT('login.or')}</p>
            <Form className="rp-open-form" layout="vertical" onFinish={onFinish} requiredMark={false}>
                <Form.Item
                    name="email"
                    label={t('openLink.auth.email')}
                    rules={[
                        { required: true, message: authT('validation.emailRequired') },
                        { type: 'email', message: authT('validation.emailInvalid') },
                    ]}
                >
                    <Input placeholder="you@example.com" autoComplete="email" prefix={<MailOutlined />} />
                </Form.Item>
                <Form.Item
                    name="password"
                    label={authT('fields.password')}
                    rules={[{ required: true, message: authT('validation.passwordRequired') }]}
                >
                    <Input.Password autoComplete="current-password" prefix={<LockOutlined />} />
                </Form.Item>
                <Button className="rp-open-primary" type="primary" htmlType="submit" loading={submitting} disabled={googleLoading}>
                    {t('openLink.auth.continue')}
                </Button>
            </Form>
            <p className="rp-open-hint">{t('openLink.auth.hint')}</p>
        </div>
    );
}

function Terminal({
    icon,
    kicker,
    title,
    lead,
    action,
    onAction,
}: {
    icon: 'hourglass' | 'ring';
    kicker: string;
    title: string;
    lead: string;
    action: string;
    onAction: () => void;
}) {
    return (
        <div className="rp-open-copy">
            <span className={`rp-open-badge${icon === 'hourglass' ? ' is-muted' : ' is-ring'}`} aria-hidden>
                {icon === 'hourglass' ? '⌛' : ''}
            </span>
            <p className="rp-open-kicker">{kicker}</p>
            <h1>{title}</h1>
            <p className="rp-open-lead">{lead}</p>
            <button className="rp-open-ghost" type="button" onClick={onAction}>
                {action}
            </button>
        </div>
    );
}

export default OpenPacket;
